import { MlTextRecord } from 'src/app/Theme/shared/service/ml-description.service';
import { parseCategoryPageName } from './category-layout.util';

export const QUOTATION_COMPONENT_KEY = 'quotation' as const;
export const QUOTATION_PAGE_NAME_KEY = 'pageName';
/** Mobile page title. Falls back to desktop `pageName` only when this key is absent. */
export const QUOTATION_PAGE_NAME_MOBILE_KEY = 'pageNameMobile';

export type QuotationLayoutDevice = 'desktop' | 'mobile';

export function quotationPageNamePayloadKey(
  device: QuotationLayoutDevice = 'desktop',
): string {
  return device === 'mobile' ? QUOTATION_PAGE_NAME_MOBILE_KEY : QUOTATION_PAGE_NAME_KEY;
}

export function defaultQuotationPayload(): Record<string, unknown> {
  return {
    [QUOTATION_PAGE_NAME_KEY]: {},
    [QUOTATION_PAGE_NAME_MOBILE_KEY]: {},
  };
}

export function parseQuotationPageName(
  payload: Record<string, unknown> | null | undefined,
  preferredLang?: string,
): string {
  return parseCategoryPageName(payload, preferredLang);
}

/** Device page title. Mobile uses `pageNameMobile` when present, else desktop `pageName`. */
export function parseQuotationPageNameForDevice(
  payload: Record<string, unknown> | null | undefined,
  preferredLang?: string,
  device: QuotationLayoutDevice = 'desktop',
): string {
  const record = parseQuotationPageNameRecordForDevice(payload, device);
  return parseQuotationPageName({ [QUOTATION_PAGE_NAME_KEY]: record }, preferredLang);
}

export function parseQuotationPageNameRecord(
  payload: Record<string, unknown> | null | undefined,
  key: string = QUOTATION_PAGE_NAME_KEY,
): MlTextRecord {
  const value = payload?.[key];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? { en: trimmed } : {};
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const record: MlTextRecord = {};
  for (const [lang, text] of Object.entries(value as Record<string, unknown>)) {
    if (typeof text === 'string' && text.trim()) {
      record[lang] = text.trim();
    }
  }
  return record;
}

/**
 * Desktop reads `pageName`. Mobile reads `pageNameMobile` when that key exists;
 * otherwise it falls back to the desktop page name.
 */
export function parseQuotationPageNameRecordForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: QuotationLayoutDevice = 'desktop',
): MlTextRecord {
  if (
    device === 'mobile' &&
    payload &&
    Object.prototype.hasOwnProperty.call(payload, QUOTATION_PAGE_NAME_MOBILE_KEY)
  ) {
    return parseQuotationPageNameRecord(payload, QUOTATION_PAGE_NAME_MOBILE_KEY);
  }
  return parseQuotationPageNameRecord(payload);
}
